import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const LanguageContext = createContext(null);

const DICT = {
  en: {
    nav: {
      about: "About",
      projects: "Projects",
      guide: "Guide",
      contact: "Contact",
      admin: "Admin",
    },
    hero: {
      overline: "Tenjara Lombok — Real Estate Development",
      title: "Refined developments,\nrooted in Lombok.",
      subtitle:
        "Premium land and thoughtfully designed properties that blend seamlessly with the island's landscape — and deliver lasting investment value.",
      cta_projects: "Explore projects",
      cta_contact: "Request a portfolio",
    },
    about: {
      overline: "Who We Are",
      title: "A modern real estate studio shaped by the island.",
      body: "At Tenjara Lombok, we are a modern real estate development company dedicated to creating exceptional investment opportunities in the heart of Lombok. Inspired by the island's natural beauty, culture, and growing potential, we specialize in the development of premium land and thoughtfully designed properties that combine elegance, sustainability, and long-term value.",
      what_overline: "What We Do",
      what_title: "Strategic real estate, from land sourcing to delivery.",
      what_body:
        "We identify and develop strategic real estate opportunities across Lombok, offering high-quality land investments and exclusive development projects tailored for both investors and lifestyle buyers. From sourcing prime locations to delivering visionary developments, our focus is on creating assets that blend seamlessly with Lombok's unique landscape while maximizing investment potential.",
      mission_overline: "Our Mission",
      mission_title: "Unlock the potential of Lombok.",
      mission_body:
        "Our mission is to unlock the potential of Lombok by delivering refined real estate developments that generate lasting value, respect the island's natural identity, and provide our clients with secure and inspiring investment opportunities. We are committed to shaping spaces that connect people, investment, and the extraordinary spirit of Lombok.",
      pillars: [
        { k: "01", title: "Premium Land", desc: "Strategically selected freehold and leasehold plots across Lombok's most coveted coastlines and hillsides." },
        { k: "02", title: "Designed Properties", desc: "Villas, estates and boutique developments crafted with architectural intent and local materials." },
        { k: "03", title: "Investor Assurance", desc: "Transparent legal structures, secure title, and clear asset performance projections." },
        { k: "04", title: "Lombok-first Ethos", desc: "Every project respects the island's natural and cultural identity — an asset class rooted in place." },
      ],
    },
    projects: {
      overline: "Portfolio",
      title: "Developments & land\nacross Lombok.",
      subtitle:
        "A curated selection of our current developments and investment-ready land offerings.",
      filter_all: "All",
      filter_in_development: "In Development",
      filter_for_sale: "For Sale",
      filter_sold: "Sold",
      status_in_development: "In Development",
      status_for_sale: "For Sale",
      status_sold: "Sold",
      view_details: "View details",
      close: "Close",
      location: "Location",
      price: "Price / Guide",
      area: "Area",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      features: "Features",
      gallery: "Gallery",
      empty: "No projects to show yet.",
      enquire: "Enquire about this project",
      dossier: "Request sales dossier",
      download_dossier: "Download dossier (PDF)",
      request_info: "Request detailed project PDF",
      request_info_message:
        "Hello Tenjara Lombok, I'd like to receive the detailed PDF for the {title} project.",
      only: "Only",
      available: "units available",
      available_short: "left",
      limited_availability: "Limited availability",
      payment_plan: "Payment facilities",
      payment_plan_title: "Flexible payment plan",
      payment_plan_sub:
        "Staged payments aligned with construction milestones — secure your unit today and pay as the project progresses.",
      payment_plan_note:
        "Approximate amounts per unit based on the entry price. Exact dates to be confirmed at signing.",
    },
    contact: {
      overline: "Get in touch",
      title: "Let's build something\nlasting in Lombok.",
      subtitle:
        "Share your plans with us — investment, development or lifestyle purchase. Our team responds to every enquiry personally.",
      name: "Full name",
      email: "Email",
      phone: "Phone / WhatsApp (optional)",
      subject: "Subject (optional)",
      message: "Tell us about your project",
      send: "Send enquiry",
      sending: "Sending…",
      sent: "Thank you. We'll be in touch shortly.",
      error: "Something went wrong. Please try again.",
      whatsapp: "WhatsApp us directly",
      channels: "Channels",
      address: "Address",
      email_label: "Email",
      phone_label: "Phone",
      social: "Social",
    },
    footer: {
      tagline: "Refined real estate, rooted in Lombok.",
      rights: "All rights reserved.",
    },
    admin: {
      login_title: "Admin Access",
      login_sub: "Sign in to manage projects and enquiries.",
      email: "Email",
      password: "Password",
      sign_in: "Sign in",
      sign_in_loading: "Signing in…",
      invalid: "Invalid email or password.",
      dashboard: "Dashboard",
      projects: "Projects",
      messages: "Messages",
      logout: "Sign out",
      new_project: "New project",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      saving: "Saving…",
      mark_read: "Mark read",
      confirm_delete: "Are you sure you want to delete this item?",
    },
  },
  es: {
    nav: {
      about: "Nosotros",
      projects: "Proyectos",
      guide: "Guía",
      contact: "Contacto",
      admin: "Admin",
    },
    hero: {
      overline: "Tenjara Lombok — Desarrollo Inmobiliario",
      title: "Desarrollos refinados,\narraigados en Lombok.",
      subtitle:
        "Terrenos premium y propiedades cuidadosamente diseñadas que se integran con el paisaje de la isla y generan valor de inversión duradero.",
      cta_projects: "Ver proyectos",
      cta_contact: "Solicitar portfolio",
    },
    about: {
      overline: "Quiénes Somos",
      title: "Un estudio inmobiliario moderno, moldeado por la isla.",
      body: "En Tenjara Lombok somos una empresa moderna de desarrollo inmobiliario dedicada a crear oportunidades de inversión excepcionales en el corazón de Lombok. Inspirados por la belleza natural, la cultura y el potencial creciente de la isla, nos especializamos en el desarrollo de terrenos premium y propiedades cuidadosamente diseñadas que combinan elegancia, sostenibilidad y valor a largo plazo.",
      what_overline: "Qué Hacemos",
      what_title: "Inmobiliaria estratégica: de la búsqueda del terreno a la entrega.",
      what_body:
        "Identificamos y desarrollamos oportunidades inmobiliarias estratégicas en Lombok, ofreciendo inversiones en terrenos de alta calidad y proyectos de desarrollo exclusivos pensados tanto para inversores como para compradores lifestyle. Desde la selección de ubicaciones privilegiadas hasta la entrega de desarrollos visionarios, nos enfocamos en crear activos que se integren con el paisaje único de Lombok maximizando su potencial de inversión.",
      mission_overline: "Nuestra Misión",
      mission_title: "Revelar el potencial de Lombok.",
      mission_body:
        "Nuestra misión es revelar el potencial de Lombok a través de desarrollos inmobiliarios refinados que generen valor duradero, respeten la identidad natural de la isla y ofrezcan a nuestros clientes oportunidades de inversión seguras e inspiradoras. Nos comprometemos a crear espacios que conecten a las personas, la inversión y el espíritu extraordinario de Lombok.",
      pillars: [
        { k: "01", title: "Terrenos Premium", desc: "Parcelas freehold y leasehold seleccionadas estratégicamente en las costas y colinas más codiciadas de Lombok." },
        { k: "02", title: "Propiedades Diseñadas", desc: "Villas, fincas y desarrollos boutique creados con intención arquitectónica y materiales locales." },
        { k: "03", title: "Seguridad al Inversor", desc: "Estructura legal transparente, títulos seguros y proyecciones claras de rendimiento del activo." },
        { k: "04", title: "Lombok Primero", desc: "Cada proyecto respeta la identidad natural y cultural de la isla — un activo enraizado en su lugar." },
      ],
    },
    projects: {
      overline: "Portfolio",
      title: "Desarrollos y terrenos\nen Lombok.",
      subtitle:
        "Una selección curada de nuestros desarrollos actuales y terrenos listos para invertir.",
      filter_all: "Todos",
      filter_in_development: "En Desarrollo",
      filter_for_sale: "En Venta",
      filter_sold: "Vendido",
      status_in_development: "En Desarrollo",
      status_for_sale: "En Venta",
      status_sold: "Vendido",
      view_details: "Ver detalles",
      close: "Cerrar",
      location: "Ubicación",
      price: "Precio / Guía",
      area: "Superficie",
      bedrooms: "Dormitorios",
      bathrooms: "Baños",
      features: "Características",
      gallery: "Galería",
      empty: "Aún no hay proyectos para mostrar.",
      enquire: "Consultar sobre este proyecto",
      dossier: "Solicitar dossier de venta",
      download_dossier: "Descargar dossier (PDF)",
      request_info: "Solicitar PDF detallado del proyecto",
      request_info_message:
        "Hola Tenjara Lombok, me gustaría recibir el PDF detallado del proyecto {title}.",
      only: "Solo quedan",
      available: "unidades disponibles",
      available_short: "disponibles",
      limited_availability: "Disponibilidad limitada",
      payment_plan: "Facilidades de pago",
      payment_plan_title: "Plan de pago flexible",
      payment_plan_sub:
        "Pagos escalonados alineados con los hitos de construcción — reserva hoy y paga a medida que avanza el proyecto.",
      payment_plan_note:
        "Importes aproximados por unidad basados en el precio de entrada. Fechas exactas por confirmar al firmar.",
    },
    contact: {
      overline: "Contacto",
      title: "Construyamos algo\nduradero en Lombok.",
      subtitle:
        "Cuéntanos tu plan — inversión, desarrollo o compra lifestyle. Nuestro equipo responde personalmente cada consulta.",
      name: "Nombre completo",
      email: "Email",
      phone: "Teléfono / WhatsApp (opcional)",
      subject: "Asunto (opcional)",
      message: "Cuéntanos sobre tu proyecto",
      send: "Enviar consulta",
      sending: "Enviando…",
      sent: "Gracias. Nos pondremos en contacto pronto.",
      error: "Ha ocurrido un error. Inténtalo de nuevo.",
      whatsapp: "Escríbenos por WhatsApp",
      channels: "Canales",
      address: "Dirección",
      email_label: "Email",
      phone_label: "Teléfono",
      social: "Redes",
    },
    footer: {
      tagline: "Inmobiliaria refinada, arraigada en Lombok.",
      rights: "Todos los derechos reservados.",
    },
    admin: {
      login_title: "Acceso Admin",
      login_sub: "Inicia sesión para gestionar proyectos y consultas.",
      email: "Email",
      password: "Contraseña",
      sign_in: "Entrar",
      sign_in_loading: "Entrando…",
      invalid: "Email o contraseña incorrectos.",
      dashboard: "Panel",
      projects: "Proyectos",
      messages: "Mensajes",
      logout: "Salir",
      new_project: "Nuevo proyecto",
      edit: "Editar",
      delete: "Eliminar",
      save: "Guardar",
      cancel: "Cancelar",
      saving: "Guardando…",
      mark_read: "Marcar leído",
      confirm_delete: "¿Seguro que quieres eliminar este elemento?",
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("tenjara_lang");
      if (saved === "en" || saved === "es") return saved;
    } catch (_) {}
    return "en";
  });

  useEffect(() => {
    try {
      localStorage.setItem("tenjara_lang", lang);
    } catch (_) {}
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l), []);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let cursor = DICT[lang];
      for (const p of parts) {
        if (cursor == null) return path;
        cursor = cursor[p];
      }
      return cursor == null ? path : cursor;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
